import { EMPTY_LIST } from '../../../../../constants/common'
import { EMAIL_RECIPIENT_OPTION_ID_SEPARATOR } from '../../../Email.constants'
import { normalizeEmail } from '../../../Email.utils'
import { getRecipientItemId } from '../../../EmailRecipientsField'
import { EMAIL_CREATION_FORM_RECIPIENT_FIELD_NAMES_IN_ORDER } from '../../EmailCreationForm.constants'
import {
  isAdditionalEmail,
  isAuthEmail,
  isPrinterRoutedRecipient,
} from '../../EmailCreationForm.utils'

function forEachRecipient(values, callback) {
  EMAIL_CREATION_FORM_RECIPIENT_FIELD_NAMES_IN_ORDER.forEach(field => {
    const list = values?.[field] || EMPTY_LIST

    list.forEach((item, index) => callback({ field, index, item }))
  })
}

function getDuplicateCheckEmail(item, printer) {
  if (isPrinterRoutedRecipient(item, printer)) {
    return null
  }

  return normalizeEmail(item?.email)
}

// Order: auth > additional > first occurrence (which is the To field by iteration order).
function selectKeeperForDuplicate(occurrences) {
  return (
    occurrences.find(({ item }) => isAuthEmail(item)) ||
    occurrences.find(({ item }) => isAdditionalEmail(item)) ||
    occurrences[0]
  )
}

function getOccurrencesByEmail({ duplicates, printer, values }) {
  const occurrencesByEmail = new Map()

  forEachRecipient(values, ({ field, index, item }) => {
    const email = getDuplicateCheckEmail(item, printer)

    if (!email || !duplicates.has(email)) {
      return
    }

    if (!occurrencesByEmail.has(email)) {
      occurrencesByEmail.set(email, [])
    }

    occurrencesByEmail.get(email).push({ field, index, item })
  })

  return occurrencesByEmail
}

function getKeeperKey(field, index) {
  return `${field}${EMAIL_RECIPIENT_OPTION_ID_SEPARATOR}${index}`
}

export function findDuplicateRecipientEmails({ printer, values }) {
  const seen = new Set()
  const duplicates = new Set()

  forEachRecipient(values, ({ item }) => {
    const email = getDuplicateCheckEmail(item, printer)

    if (!email) {
      return
    }

    if (seen.has(email)) {
      duplicates.add(email)
    } else {
      seen.add(email)
    }
  })

  return duplicates
}

export function getDuplicateRecipientOptionKeys({ printer, values }) {
  const duplicates = findDuplicateRecipientEmails({ printer, values })

  if (duplicates.size === 0) {
    return new Set()
  }

  const keys = new Set()

  forEachRecipient(values, ({ item }) => {
    const email = getDuplicateCheckEmail(item, printer)

    if (email && duplicates.has(email)) {
      keys.add(getRecipientItemId(item, { fallbackToEmail: true }))
    }
  })

  return keys
}

// Removes duplicate recipients across To/CC/BCC fields,
// keeping the highest-priority occurrence of each email (auth > additional > partner).
export function removeDuplicateRecipientEmails({ printer, values }) {
  const duplicates = findDuplicateRecipientEmails({ printer, values })

  if (duplicates.size === 0) {
    return values
  }

  const occurrencesByEmail = getOccurrencesByEmail({
    duplicates,
    printer,
    values,
  })

  const keeperKeys = new Set(
    [...occurrencesByEmail.values()].map(occurrences => {
      const { field, index } = selectKeeperForDuplicate(occurrences)

      return getKeeperKey(field, index)
    })
  )

  const filteredFields =
    EMAIL_CREATION_FORM_RECIPIENT_FIELD_NAMES_IN_ORDER.reduce((acc, field) => {
      const list = values?.[field] || EMPTY_LIST

      acc[field] = list.filter((item, index) => {
        const email = getDuplicateCheckEmail(item, printer)

        return (
          !email ||
          !duplicates.has(email) ||
          keeperKeys.has(getKeeperKey(field, index))
        )
      })

      return acc
    }, {})

  return { ...values, ...filteredFields }
}
